import { useState } from "react";
import { useSessionAccount } from "@/lib/useSessionAccount";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { AuthRequired } from "@/components/auth/AuthRequired";
import { fetchMeOrNull, logout } from "@/lib/auth";
import { fetchProfile, keys, PREF_AUTO_REDEEM_ENABLED, PREF_ONE_CLICK_SELL_ENABLED } from "@/lib/queries";
import { selfIdentity } from "@/lib/selfIdentity";
import { usePreferences, prefBool, useSetPreference } from "@/lib/usePreferences";
import { useLanguage } from "@/components/i18n/LanguageProvider";
import { LanguagePickerDialog } from "@/components/i18n/LanguagePicker";
import {
  Avatar,
  Button,
  Card,
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui";
import { ContentTopBar, TopBarTitle } from "@/components/layout/ContentTopBar";
import { SettingsGroup, RowLink, RowButton, RowToggle } from "@/components/profile/SettingsKit";
import { Clock, FileText, LogOut, ChevronRight, Globe } from "@/icons";
import { Lock, Shield, Headphones, SquarePen, Zap, HandCoins, Trash2 } from "lucide-react";
import { buildHead } from "@/lib/seo";

export const Route = createFileRoute("/_app/settings")({
  head: () =>
    buildHead({ path: "/settings", title: "Settings", noindex: true }),
  component: () => (
    <AuthRequired>
      <ProfileHome />
    </AuthRequired>
  ),
});

function ProfileHome() {
  const { t } = useTranslation("settings");
  const qc = useQueryClient();
  const me = useQuery({ queryKey: keys.me(), queryFn: fetchMeOrNull });
  const profile = useQuery({ queryKey: keys.profile(), queryFn: fetchProfile, enabled: !!me.data });
  // SM-811: the name the user set, else a launched user's provider id, else
  // the email, else "Your account" — the sidebar and account menu agree.
  const { name, email } = selfIdentity(profile.data, me.data);

  // Auto-redeem + 1-Click Sell are server-authoritative preferences, so
  // the toggles follow the account across devices.
  const prefs = usePreferences();
  const autoRedeem = prefBool(prefs.data, PREF_AUTO_REDEEM_ENABLED);
  const oneClickSell = prefBool(prefs.data, PREF_ONE_CLICK_SELL_ENABLED);
  const setPref = useSetPreference();
  // SM-653 (15): an external-credit account's auto-redeem is forced ON and
  // not user-overridable (SM-625) — no toggle is drawn for it. The server
  // refuses the preference change regardless.
  // SM-811: a provider-launched user has no password or second factor here
  // (SM-640) — the Password & 2FA row is not drawn for them.
  const { autoRedeemConfigurable, externalLaunch } = useSessionAccount();

  // Language row — shows the active language's native name and opens the
  // shared picker (SM-286).
  const { locale, languages } = useLanguage();
  const [langOpen, setLangOpen] = useState(false);
  const activeLanguage = languages.find((l) => l.code === locale);

  const [logoutOpen, setLogoutOpen] = useState(false);

  const doLogout = useMutation({
    mutationFn: logout,
    onSuccess: async () => {
      await qc.invalidateQueries();
      window.location.assign("/");
    },
  });

  return (
    <div className="py-2 lg:pt-0">
      {/* full-width top bar — title left, bell + balance pinned top-right */}
      <ContentTopBar className="lg:-mt-4" divider>
        <TopBarTitle>{t("title")}</TopBarTitle>
      </ContentTopBar>

      {/* settings list — capped + centred under the full-width bar */}
      <div className="mx-auto max-w-2xl">
        {/* identity header — edit pencil → change display name + photo */}
        <Card className="mb-5 flex items-center gap-4 p-4">
          <Avatar src={profile.data?.avatar_url} name={name} size={56} className="rounded-full" />
          <div className="min-w-0 flex-1">
            {/* Two lines: a provider user id is one long unbroken token (up
                to 256 characters), so it breaks mid-token; a name with
                spaces still wraps at a space. */}
            <p title={name} className="line-clamp-2 break-words font-display text-lg font-bold leading-tight">{name}</p>
            {email && <p className="truncate text-sm text-fg-muted">{email}</p>}
          </div>
          <Link
            to="/settings/edit"
            aria-label={t("editProfile")}
            className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-surface-2 text-fg-muted transition-colors hover:text-fg"
          >
            <SquarePen className="h-4 w-4" />
          </Link>
        </Card>

        <SettingsGroup title={t("groups.general")}>
          <RowToggle
            icon={<Zap className="h-4 w-4 text-warning" />}
            label={t("rows.oneClickSell")}
            checked={oneClickSell}
            onCheckedChange={(v) => setPref.mutate({ [PREF_ONE_CLICK_SELL_ENABLED]: v })}
          />
          {autoRedeemConfigurable ? (
            <RowToggle
              icon={<HandCoins className="h-4 w-4 text-warning" />}
              label={t("rows.autoRedeem")}
              checked={autoRedeem}
              onCheckedChange={(v) => setPref.mutate({ [PREF_AUTO_REDEEM_ENABLED]: v })}
            />
          ) : null}
          <RowButton
            icon={<Globe className="h-4 w-4" />}
            label={t("rows.language")}
            onClick={() => setLangOpen(true)}
            trailing={
              <span className="flex shrink-0 items-center gap-2 text-sm text-fg-muted">
                {activeLanguage?.native_name || activeLanguage?.name || locale}
                <ChevronRight className="h-4 w-4 shrink-0 text-fg-subtle" />
              </span>
            }
          />
        </SettingsGroup>

        <SettingsGroup title={t("groups.accountSecurity")}>
          {externalLaunch ? null : (
            <RowLink to="/settings/security" icon={<Shield className="h-4 w-4" />} label={t("rows.passwordMfa")} />
          )}
          <RowLink to="/settings/activity" icon={<Clock className="h-4 w-4" />} label={t("rows.activity")} />
          <RowLink to="/settings/delete" icon={<Trash2 className="h-4 w-4" />} label={t("rows.deleteAccount")} danger />
        </SettingsGroup>

        <SettingsGroup title={t("groups.legal")}>
          <RowLink to="/settings/privacy" icon={<Lock className="h-4 w-4" />} label={t("rows.privacy")} />
          <RowLink to="/settings/terms" icon={<FileText className="h-4 w-4" />} label={t("rows.terms")} />
        </SettingsGroup>

        <SettingsGroup title={t("groups.others")}>
          <RowLink to="/settings/help" icon={<Headphones className="h-4 w-4" />} label={t("rows.help")} />
          <RowButton
            icon={<LogOut className="h-4 w-4" />}
            label={t("rows.logOut")}
            danger
            onClick={() => setLogoutOpen(true)}
            trailing={<span />}
          />
        </SettingsGroup>
      </div>

      <LanguagePickerDialog open={langOpen} onOpenChange={setLangOpen} />

      <Dialog open={logoutOpen} onOpenChange={setLogoutOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("common:account.logOutTitle")}</DialogTitle>
            <DialogDescription>{t("common:account.logOutBody")}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost">{t("common:actions.cancel")}</Button>
            </DialogClose>
            <Button variant="destructive" loading={doLogout.isPending} onClick={() => doLogout.mutate()}>
              {t("common:actions.logOut")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
